/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-logo-picker.js — Logo picker step (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `logo_picker`. Doc 4 §3 saved-logos read path,
 * Doc 4 §5 Worker write paths. Doc 3 §4.6-7 upload events.
 *
 * Phase 2.5 IA rebuild per mockups/step4-logo-picker-{desktop,mobile}.html:
 *   Replaces the previous 3-tab (Existing | Upload | Text) layout with
 *   the "Upload-primary + disclosure" pattern:
 *     1. Dropzone primary at top — drag/drop OR click-to-browse
 *     2. Upload preview state (when a file has been uploaded successfully) —
 *        green-success card with filename, "Uploaded" badge, and a Remove
 *        button that returns to the dropzone state
 *     3. Trust line below
 *     4. Existing-disclosure collapsible: button shows count badge ("3
 *        saved"), expands a 3-column grid of saved logo cards
 *     5. Text-pivot button at bottom: "Don't have a logo file? Create a
 *        text logo instead" → goToStep('logo_text_builder')
 *     6. Notes textarea (optional, Phase 2.5 renders but doesn't wire to
 *        state — see Phase 5 polish note below)
 *
 * State paths per Doc 6 §5.3 (UNCHANGED from previous version):
 *   state.draft.logo_assignments = {
 *     [positionId]: { source: 'existing' | 'upload' | 'text', logoId, ... }
 *   }
 *   state.draft._position_cursor       (orchestrator-owned)
 *   state.draft._current_position_for_logo  (orchestrator-owned)
 *
 * Logo persistence (CRITICAL — preserved from commit b8e6565):
 *   Upload-success handler MUST call JRS.LogoService.saveLogo() BEFORE
 *   dispatching to state.draft.logo_assignments. saveLogo writes to the
 *   Worker metafield (logged-in) or localStorage (guest) so the logo
 *   id resolves to a real saved-logo entry. Cart-submit (follow-up
 *   session) depends on this.
 *
 * Doc 4 v0.3 §5.4 — selecting an existing logo also fires touchLogo() to
 * refresh last_used_at server-side.
 *
 * Phase 5 polish items deferred:
 *   - Notes textarea: rendered per mockup but not wired to state. No
 *     `notes` field exists in Doc 6 §5.3 state shape; adding it would
 *     require a state-shape expansion + cart-payload-builder review.
 *     Phase 5 follow-up.
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'logo_picker';

  var _mountEl = null;
  var _listeners = [];
  var _disclosureExpanded = false;
  // Per-mount tracker: prevents double-firing pdp_customizer_login_nudge_shown
  // if the nudge re-renders or toggles visibility multiple times in one mount.
  var _shownNudgesThisMount = {};

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopup.setReassurance(
      'Every logo gets a free design proof before we make anything.'
    );
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var draft = state.draft || {};
    var pid = _resolveCurrentPositionId(draft);
    var savedLogosResult = (JRS.LogoService && JRS.LogoService.readSavedLogos)
      ? JRS.LogoService.readSavedLogos()
      : { logos: [], isLoggedIn: false, hasCloudLogos: false };

    var currentAssignment = (draft.logo_assignments || {})[pid] || null;

    // 2026-05-20 Bug 6 fix — when re-entering logo_picker (via review-edit
    // or back-arrow) with an existing-logo assignment already in state,
    // auto-expand the saved-logo disclosure so the customer sees their
    // pre-selected card without having to click "Use a saved logo". The
    // selected styling is already applied per the currentAssignment
    // check in _renderExistingDisclosure.
    if (currentAssignment && currentAssignment.source === 'existing') {
      _disclosureExpanded = true;
    }

    _shownNudgesThisMount = {};
    _render(pid, savedLogosResult, currentAssignment);
    _bindInteractions();
    _syncCta(true); // chrome-footer Continue CTA (label + enable on logo assignment)
    _firePostRenderShownPass();
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var draft = state.draft || {};
    var pid = _resolveCurrentPositionId(draft);
    var assignments = draft.logo_assignments || {};
    if (!pid || !assignments[pid]) {
      _showError('Add a logo to continue.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
        step_id: STEP_ID,
        field_id: 'logo_assignments[' + pid + ']',
        reason: 'logo_required'
      });
      return { valid: false, errors: { logo_assignments: 'logo_required' } };
    }
    _clearError();
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Position resolution
  // ---------------------------------------------------------------------

  function _resolveCurrentPositionId(draft) {
    var positions = draft.positions || [];
    // Honour the pin only while it is still a selected position. A review Edit
    // pins a position; swapping it out in the position picker used to leave the
    // pin on the removed position, so validation passed on its old logo and the
    // new position reached Confirm with none (logo_setup_missing).
    if (draft._current_position_for_logo &&
        positions.indexOf(draft._current_position_for_logo) !== -1) {
      return draft._current_position_for_logo;
    }
    if (positions.length === 0) return null;
    var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
    if (cursor < 0 || cursor >= positions.length) cursor = 0;
    return positions[cursor];
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _render(positionId, savedLogosResult, currentAssignment) {
    var hasUpload = currentAssignment && currentAssignment.source === 'upload';
    var hasText = currentAssignment && currentAssignment.source === 'text';
    var savedCount = savedLogosResult.logos.length;
    var isLoggedIn = (savedLogosResult.isLoggedIn === true) ||
      (JRS.LogoService && typeof JRS.LogoService.isLoggedIn === 'function' && JRS.LogoService.isLoggedIn());

    // Doc 4 §7.1 — three login nudges across the popup, two of them in this
    // step. Each renders only for guests and only if not session-dismissed.
    var renderEmptyExistingNudge = !isLoggedIn && savedCount === 0 &&
      !_isNudgeDismissed('logo_picker_empty_existing');
    var renderPostUploadNudge = !isLoggedIn &&
      !_isNudgeDismissed('logo_picker_post_upload');

    // 2026-05-20 Bug 6.1 fix — when looping per-position under
    // different_logos, the picker re-mounted with the same header for
    // every iteration so the customer couldn't tell which position
    // they were configuring. Now we render a position context pill
    // ("Position X of N") above a position-specific title ("Logo for
    // Left Breast"). Same_logo and single-position flows keep the
    // existing static header.
    var headerHtml = _renderHeaderForCurrentPosition(positionId);

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--logo-picker" data-jrs-step="logo_picker">' +
           headerHtml +
      '  <section class="jrs-step__body">' +
           _renderDropzone(hasUpload || hasText) +
           _renderUploadPreview(currentAssignment, hasUpload) +
           (hasText ? _renderTextAddedCard(positionId) : '') +
           (renderPostUploadNudge
             ? _renderLoginNudge(
                 'logo_picker_post_upload',
                 'Sign in to save this logo for future orders.',
                 { hidden: !hasUpload }
               )
             : '') +
      '    <p data-jrs-logo-error class="jrs-step__error" hidden role="alert"></p>' +
           (savedCount > 0 ? _renderExistingDisclosure(savedLogosResult, currentAssignment) : '') +
           (renderEmptyExistingNudge
             ? _renderLoginNudge(
                 'logo_picker_empty_existing',
                 'Sign in to access your saved logos.',
                 {}
               )
             : '') +
      '    <button type="button" class="jrs-text-pivot" data-jrs-action="goto-text-builder">' +
      '      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '        <path d="M5 7 L19 7 M12 7 L12 19 M9 19 L15 19"/>' +
      '      </svg>' +
      '      No logo file? Click here to create one from text' +
      '    </button>' +
      '    <div class="jrs-notes">' +
      '      <label class="jrs-notes__label" for="jrs-notes-input">' +
      '        Notes for our team' +
      '        <span class="jrs-notes__hint">(optional)</span>' +
      '      </label>' +
      '      <textarea id="jrs-notes-input" class="jrs-notes__textarea" rows="2" placeholder="e.g. Please match the navy blue exactly" data-jrs-action="notes-input">' +
             _escape((currentAssignment && currentAssignment.note) || '') +
           '</textarea>' +
      '    </div>' +
      '  </section>' +
      '</div>';
  }

  // per_product: returns the category context (name + "{n} of {N}" cursor) for
  // the header; bundle_wide returns { perProduct: false } so the header renders
  // unchanged. Mirrors position_picker._headerInfo (spec D5).
  function _categoryProgress(state) {
    var draft = (state && state.draft) || {};
    if (draft.branding_strategy !== 'per_product') return { perProduct: false };
    var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
      ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    var cat = active[cursor];
    return {
      perProduct: true,
      categoryName: (cat && cat.name) || 'this product',
      current: cursor + 1,
      total: active.length || 1
    };
  }

  /**
   * 2026-05-20 Bug 6.1 fix — position-aware header for the logo picker.
   *
   * Behaviour:
   *   - logo_strategy === 'different_logos': render a "Position X of N"
   *     pill above a position-specific title ("Logo for Left Breast").
   *     Subtitle keeps the upload guidance.
   *   - Single position OR same_logo strategy: render the original
   *     static header ("Add your logo" + subtitle). Customer doesn't
   *     need position context because the same logo applies everywhere.
   *   - Position name resolved from JRS.PositionCatalogue.getById, with
   *     graceful fallback to the raw positionId when the catalogue lookup
   *     misses (defensive against an unrecognised position id from a
   *     drift).
   */
  function _renderHeaderForCurrentPosition(positionId) {
    var state = JRS.CustomizerPopupState.getState();
    var draft = (state && state.draft) || {};
    var positions = draft.positions || [];
    var strategy = draft.logo_strategy;
    var isDifferentLogos = (strategy === 'different_logos') && positions.length >= 2;

    // Per-product bundle (Phase per_product S2): prepend category context — a
    // "{n} of {N}" pill (which product in the bundle) + the category name as the
    // sub-label — while keeping the position-specific title. Bundle-wide /
    // multistep fall through to the two unchanged branches below
    // (catInfo.perProduct is false). When uploading per position, the position
    // step is folded into the sub-label so the customer still sees "logo x of n".
    var catInfo = _categoryProgress(state);
    if (catInfo.perProduct) {
      var ppPositionName = positionId || '';
      if (positionId && JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getById === 'function') {
        var ppDescriptor = JRS.PositionCatalogue.getById(positionId);
        if (ppDescriptor && ppDescriptor.name) ppPositionName = ppDescriptor.name;
      }
      var ppCursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
      var ppTitle = isDifferentLogos ? ('Logo for ' + _escape(ppPositionName)) : 'Add your logo';
      var ppSub = isDifferentLogos
        ? _escape(catInfo.categoryName) + ' · logo ' + (ppCursor + 1) + ' of ' + positions.length
        : _escape(catInfo.categoryName);
      return (
        '  <header class="jrs-step__header jrs-step__header--per-position">' +
        '    <p class="jrs-step__progress-pill" aria-label="Product ' + catInfo.current + ' of ' + catInfo.total + '">' +
        '      ' + catInfo.current + ' of ' + catInfo.total +
        '    </p>' +
        '    <h2 class="jrs-step__title">' + ppTitle + '</h2>' +
        '    <p class="jrs-step__subtitle">' + ppSub + '</p>' +
        '  </header>'
      );
    }

    if (!isDifferentLogos) {
      return (
        '  <header class="jrs-step__header">' +
        '    <h2 class="jrs-step__title">Add your logo</h2>' +
        '  </header>'
      );
    }

    var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
    var stepNum = cursor + 1; // 1-based for display
    var totalPositions = positions.length;

    var positionName = positionId || '';
    if (positionId && JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getById === 'function') {
      var descriptor = JRS.PositionCatalogue.getById(positionId);
      if (descriptor && descriptor.name) positionName = descriptor.name;
    }

    return (
      '  <header class="jrs-step__header jrs-step__header--per-position">' +
      '    <p class="jrs-step__progress-pill" aria-label="Logo step ' + stepNum + ' of ' + totalPositions + '">' +
      '      Position ' + stepNum + ' of ' + totalPositions +
      '    </p>' +
      '    <h2 class="jrs-step__title">Logo for ' + _escape(positionName) + '</h2>' +
      '    <p class="jrs-step__subtitle">Upload a logo file for this position. You\'ll pick the next one after.</p>' +
      '  </header>'
    );
  }

  function _renderDropzone(hidden) {
    return (
      '<label class="jrs-dropzone' + (hidden ? ' jrs-dropzone--hidden' : '') + '" data-jrs-dropzone tabindex="0">' +
      '  <input type="file" class="jrs-dropzone__input"' +
      '         accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp,application/pdf,.pdf"' +
      '         data-jrs-action="upload-file"' +
      '         aria-describedby="jrs-dropzone-formats">' +
      '  <svg class="jrs-dropzone__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '    <path d="M24 30 L24 8"/>' +
      '    <path d="M14 18 L24 8 L34 18"/>' +
      '    <path d="M8 32 L8 38 C8 39.1 8.9 40 10 40 L38 40 C39.1 40 40 39.1 40 38 L40 32"/>' +
      '  </svg>' +
      // Both wordings ship in the markup; jrs-v2-steps.css shows one per
      // breakpoint (--tap below 640px, --drop plus the hint above it). A phone
      // can't drag a file and the whole label is already the tap target, so the
      // "or browse files" line is redundant there. CSS-only on purpose: no
      // device sniffing on this step, and the hidden line is display:none so
      // assistive tech reads one title, not two.
      '  <p class="jrs-dropzone__title jrs-dropzone__title--tap">Tap to upload your logo</p>' +
      '  <p class="jrs-dropzone__title jrs-dropzone__title--drop">Drop your logo here</p>' +
      '  <p class="jrs-dropzone__hint">or <strong>browse files</strong></p>' +
      '  <p class="jrs-dropzone__formats" id="jrs-dropzone-formats">Most image files work — JPG, PNG, PDF and more. Up to 10 MB.</p>' +
      '</label>' +
      '<div data-jrs-upload-progress class="jrs-upload-progress" hidden>' +
      '  <span class="jrs-upload-progress__text">Uploading…</span>' +
      '  <div class="jrs-upload-progress__bar"><span data-jrs-upload-progress-bar></span></div>' +
      '</div>'
    );
  }

  function _renderUploadPreview(assignment, visible) {
    var filename = '';
    var url = '';
    if (assignment) {
      filename = assignment.filename || assignment.logoId || 'Logo';
      url = assignment.url || '';
    }
    // 2026-05-20 Bug 6 fix — when re-entering logo_picker via review-edit
    // or via the back-arrow, show the actual logo thumbnail in the
    // preview instead of the generic placeholder SVG. Customer was
    // seeing "Logo: filename.png" with no image and assuming nothing
    // was pre-selected.
    var thumbInner;
    if (url) {
      thumbInner =
        '<img class="jrs-upload-preview__thumb-img" alt="" loading="lazy" src="' + _escape(_previewSrc(url)) + '">';
    } else {
      thumbInner =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '  <rect x="3" y="3" width="18" height="18" rx="2"/>' +
        '  <circle cx="9" cy="9" r="2" fill="currentColor"/>' +
        '  <path d="M21 15 L16 10 L5 21"/>' +
        '</svg>';
    }
    return (
      '<div data-jrs-upload-preview class="jrs-upload-preview' + (visible ? ' jrs-upload-preview--visible' : '') + '" role="status" aria-live="polite">' +
      '  <div class="jrs-upload-preview__thumb" aria-hidden="true">' +
           thumbInner +
      '  </div>' +
      '  <div class="jrs-upload-preview__content">' +
      '    <p class="jrs-upload-preview__filename">' + _escape(filename) + '</p>' +
      '    <span class="jrs-upload-preview__success">' +
      '      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M3 8 L7 12 L13 4"/></svg>' +
      '      Uploaded' +
      '    </span>' +
      '  </div>' +
      '  <button type="button" class="jrs-upload-preview__remove" data-jrs-action="remove-upload" aria-label="Remove and re-upload">' +
      '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6 L18 18 M18 6 L6 18"/></svg>' +
      '  </button>' +
      '</div>'
    );
  }

  // Text logo "added" confirmation card — the text-source analogue of the upload
  // success card (_renderUploadPreview). After the text builder commits a text logo
  // and returns here, a text assignment otherwise fell through to the empty dropzone
  // with no signal it took (only the chrome Continue CTA silently enabled). This
  // renders a clear green success card in the dropzone's place: text preview +
  // "Text logo added" badge + Edit (back to the builder) + Remove. Renders from
  // draft.text_logo (the single source cart-submit reads at jrs-v2-cart-submit.js)
  // with the per-position previewText as a fallback — no assignment-shape or
  // cart-payload change.
  function _renderTextAddedCard(positionId) {
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var assignment = (draft.logo_assignments || {})[positionId] || {};
    var tl = draft.text_logo || {};
    var nonEmpty = [];
    var srcLines = (tl.lines && tl.lines.length) ? tl.lines : [];
    for (var i = 0; i < srcLines.length; i++) {
      if (srcLines[i] && String(srcLines[i]).replace(/\s+/g, '').length) nonEmpty.push(srcLines[i]);
    }
    if (!nonEmpty.length && assignment.previewText) nonEmpty.push(assignment.previewText);
    var color = tl.color || '#1A1A1A';
    var isItalic = String(tl.font || '').toLowerCase() === 'italic';
    var primary = nonEmpty.length ? nonEmpty[0] : 'Your text';

    var tileLines = '';
    for (var j = 0; j < nonEmpty.length && j < 3; j++) {
      tileLines += '<span class="jrs-text-added__tile-line">' + _escape(nonEmpty[j]) + '</span>';
    }

    return (
      '<div class="jrs-text-added" data-jrs-text-added role="status" aria-live="polite">' +
      '  <div class="jrs-text-added__tile" aria-hidden="true" style="color: ' + _escape(color) + ';' + (isItalic ? ' font-style: italic;' : '') + '">' +
           tileLines +
      '  </div>' +
      '  <div class="jrs-text-added__content">' +
      '    <p class="jrs-text-added__name">' + _escape(primary) + '</p>' +
      '    <span class="jrs-text-added__success">' +
      '      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M3 8 L7 12 L13 4"/></svg>' +
      '      Text logo added' +
      '    </span>' +
      '  </div>' +
      '  <div class="jrs-text-added__actions">' +
      '    <button type="button" class="jrs-text-added__edit" data-jrs-action="edit-text">Edit</button>' +
      '    <button type="button" class="jrs-text-added__remove" data-jrs-action="remove-text" aria-label="Remove text logo">' +
      '      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6 L18 18 M18 6 L6 18"/></svg>' +
      '    </button>' +
      '  </div>' +
      '</div>'
    );
  }

  function _renderExistingDisclosure(savedLogosResult, currentAssignment) {
    var currentId = (currentAssignment && currentAssignment.source === 'existing')
      ? currentAssignment.logoId
      : null;

    var cards = '';
    for (var i = 0; i < savedLogosResult.logos.length; i++) {
      var logo = savedLogosResult.logos[i];
      var selected = currentId === logo.id;
      var thumbHtml = logo.type === 'text'
        ? '<span class="jrs-existing-card__thumb-text">' + _escape((logo.text_lines && logo.text_lines[0]) || 'Text') + '</span>'
        : '<img alt="" loading="lazy" src="' + _escape(_previewSrc(logo.url, logo.filename)) + '">';
      var dateLabel = _formatRelativeDate(logo.last_used_at || logo.uploaded_at);

      cards +=
        '<button type="button"' +
        '        class="jrs-existing-card' + (selected ? ' jrs-existing-card--selected' : '') + '"' +
        '        role="checkbox" aria-checked="' + (selected ? 'true' : 'false') + '"' +
        '        data-jrs-action="select-existing" data-jrs-logo-id="' + _escape(logo.id) + '">' +
        '  <span class="jrs-existing-card__thumb" aria-hidden="true">' + thumbHtml + '</span>' +
        '  <p class="jrs-existing-card__name">' + _escape(logo.filename || ('Logo ' + logo.id)) + '</p>' +
        '  <p class="jrs-existing-card__date">' + _escape(dateLabel) + '</p>' +
        '</button>';
    }

    return (
      '<div class="jrs-existing-disclosure">' +
      '  <button type="button" class="jrs-existing-toggle"' +
      '          data-jrs-action="toggle-existing"' +
      '          aria-expanded="' + (_disclosureExpanded ? 'true' : 'false') + '"' +
      '          aria-controls="jrs-existing-grid">' +
      '    <span>Use a saved logo' +
      '      <span class="jrs-existing-toggle__count">(' + savedLogosResult.logos.length + ' saved)</span>' +
      '    </span>' +
      '    <svg class="jrs-existing-toggle__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9 L12 15 L18 9"/></svg>' +
      '  </button>' +
      '  <div id="jrs-existing-grid" class="jrs-existing-grid" data-expanded="' + (_disclosureExpanded ? '1' : '0') + '" role="group" aria-label="Saved logos">' +
            cards +
      '  </div>' +
      '</div>'
    );
  }

  function _showError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-logo-error]');
    if (err) { err.textContent = msg; err.hidden = false; }
  }

  function _clearError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-logo-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
  }

  // The assigned-state label names where the press actually goes. This is a
  // READ-ONLY mirror of _onContinueClick's own routing — it dispatches nothing
  // and adds no counter; it reads the same cursor fields that handler reads:
  //   - different_logos with positions still to go → another logo_picker
  //     ("Next logo")
  //   - per_product bundle with categories still to go → the next product's
  //     position_picker. Neither "Next logo" nor "Review…" is true there, so
  //     it keeps the neutral "Continue".
  //   - everything else → review ("Review and add to cart")
  // Keep this in step with _onContinueClick if that routing ever changes.
  function _assignedLabel(draft) {
    draft = draft || {};
    if (draft.logo_strategy === 'different_logos') {
      var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
      if (cursor + 1 < (draft.positions || []).length) return 'Next logo';
    }
    if (draft.branding_strategy === 'per_product' &&
        !(typeof draft._edit_category_index === 'number' && draft._edit_category_index >= 0)) {
      var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
        ? (JRS.CustomizerPopupState.getActiveCategoryList() || []) : [];
      var catCursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
        ? draft._current_category_index : 0;
      if (active.length && catCursor + 1 < active.length) return 'Continue';
    }
    return 'Review and add to cart';
  }

  // Drive the shell's chrome-footer CTA on entry: a label naming the next
  // screen + disabled until a logo is assigned to the current position.
  // Derives hasAssignment from state (correct at mount/re-mount time).
  function _syncCta(initial) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var pid = _resolveCurrentPositionId(draft);
    var hasAssignment = !!(pid && (draft.logo_assignments || {})[pid]);
    var label = hasAssignment ? _assignedLabel(draft) : 'Add a logo to continue';
    if (initial && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({ label: label, disabled: !hasAssignment, onClick: _onContinueClick });
    } else if (typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ label: label, disabled: !hasAssignment });
    }
  }

  // Called by the assignment-change paths (upload success, select-existing,
  // remove). Patches the chrome CTA from the passed flag — these sites pass
  // the new state explicitly, so we don't re-derive (avoids any dispatch-
  // timing race).
  function _updateCtaState(hasAssignment) {
    var pop = JRS.CustomizerPopup;
    if (!pop || typeof pop.updatePrimaryCta !== 'function') return;
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    pop.updatePrimaryCta({
      label: hasAssignment ? _assignedLabel(draft) : 'Add a logo to continue',
      disabled: !hasAssignment
    });
  }

  function _swapToUploadPreview(filename, url) {
    if (!_mountEl) return;
    var dropzone = _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) dropzone.classList.add('jrs-dropzone--hidden');
    var preview = _mountEl.querySelector('[data-jrs-upload-preview]');
    if (preview) {
      preview.classList.add('jrs-upload-preview--visible');
      var nameEl = preview.querySelector('.jrs-upload-preview__filename');
      if (nameEl) nameEl.textContent = filename || 'Logo';
      // 2026-05-20 Bug 6 fix — swap the thumbnail SVG for the actual
      // uploaded image when the URL is available (post-upload success
      // path passes it through; old behaviour just kept the generic
      // placeholder SVG even after the upload completed).
      if (url) {
        var thumb = preview.querySelector('.jrs-upload-preview__thumb');
        if (thumb) {
          thumb.innerHTML = '<img class="jrs-upload-preview__thumb-img" alt="" loading="lazy" src="' + _escape(_previewSrc(url)) + '">';
        }
      }
    }
    // Reveal the post-upload login nudge (guest-only, rendered hidden initially)
    // and fire its first-shown event the first time it becomes visible.
    var postUploadNudge = _mountEl.querySelector('[data-jrs-login-nudge][data-placement="logo_picker_post_upload"]');
    if (postUploadNudge) {
      postUploadNudge.classList.remove('jrs-login-nudge--hidden');
      _maybeFireNudgeShown('logo_picker_post_upload');
    }
  }

  function _swapToDropzone() {
    if (!_mountEl) return;
    var dropzone = _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) {
      dropzone.classList.remove('jrs-dropzone--hidden');
      var input = dropzone.querySelector('.jrs-dropzone__input');
      if (input) input.value = '';
    }
    var preview = _mountEl.querySelector('[data-jrs-upload-preview]');
    if (preview) preview.classList.remove('jrs-upload-preview--visible');
    // Hide the post-upload login nudge alongside the preview.
    var postUploadNudge = _mountEl.querySelector('[data-jrs-login-nudge][data-placement="logo_picker_post_upload"]');
    if (postUploadNudge) postUploadNudge.classList.add('jrs-login-nudge--hidden');
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;

    var fileInput = _mountEl.querySelector('[data-jrs-action="upload-file"]');
    if (fileInput) _bind(fileInput, 'change', _onFileChange);

    // Drag and drop on the dropzone.
    var dropzone = _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) {
      _bind(dropzone, 'dragover', _onDragOver);
      _bind(dropzone, 'dragleave', _onDragLeave);
      _bind(dropzone, 'drop', _onDrop);
    }

    var removeBtn = _mountEl.querySelector('[data-jrs-action="remove-upload"]');
    if (removeBtn) _bind(removeBtn, 'click', _onRemoveUpload);

    var toggle = _mountEl.querySelector('[data-jrs-action="toggle-existing"]');
    if (toggle) _bind(toggle, 'click', _onToggleExisting);

    var existingCards = _mountEl.querySelectorAll('[data-jrs-action="select-existing"]');
    for (var i = 0; i < existingCards.length; i++) {
      _bind(existingCards[i], 'click', _onSelectExistingClick);
    }

    var textPivot = _mountEl.querySelector('[data-jrs-action="goto-text-builder"]');
    if (textPivot) _bind(textPivot, 'click', _onGoToTextBuilderClick);

    var editText = _mountEl.querySelector('[data-jrs-action="edit-text"]');
    if (editText) _bind(editText, 'click', _onEditTextClick);

    var removeText = _mountEl.querySelector('[data-jrs-action="remove-text"]');
    if (removeText) _bind(removeText, 'click', _onRemoveText);

    // §3 (2026-08-04) — notes textarea is now WIRED: value flows to the
    // current position's assignment.note → cart-submit → visible 'Note N'.
    var notesInput = _mountEl.querySelector('[data-jrs-action="notes-input"]');
    if (notesInput) _bind(notesInput, 'change', _syncNoteToAssignment);

    _bindNudgeInteractions();
  }

  function _onDragOver(e) {
    e.preventDefault();
    var dropzone = _mountEl && _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) dropzone.classList.add('jrs-dropzone--dragover');
  }

  function _onDragLeave(e) {
    var dropzone = _mountEl && _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) dropzone.classList.remove('jrs-dropzone--dragover');
  }

  function _onDrop(e) {
    e.preventDefault();
    var dropzone = _mountEl && _mountEl.querySelector('[data-jrs-dropzone]');
    if (dropzone) dropzone.classList.remove('jrs-dropzone--dragover');
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
      _processFile(e.dataTransfer.files[0]);
    }
  }

  function _onFileChange(e) {
    var file = e.target && e.target.files && e.target.files[0];
    if (file) _processFile(file);
  }

  function _processFile(file) {
    if (!JRS.LogoService) return;

    var validation = JRS.LogoService.validateFile(file);
    if (!validation.valid) {
      _showError(validation.error || 'File rejected.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_logo_upload_failed', {
        step_id: STEP_ID,
        error_key: validation.errorKey,
        error_message: validation.error
      });
      return;
    }
    _clearError();

    var progressEl = _mountEl.querySelector('[data-jrs-upload-progress]');
    var progressBar = _mountEl.querySelector('[data-jrs-upload-progress-bar]');
    if (progressEl) progressEl.hidden = false;

    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var pid = _resolveCurrentPositionId(draft);

    JRS.LogoService.uploadFile(file, function (percent) {
      if (progressBar) progressBar.style.width = percent + '%';
    }).then(function (result) {
      if (progressEl) progressEl.hidden = true;
      if (!pid) return;

      // 2026-05-18 (commit b8e6565) — persist BEFORE state dispatch.
      // saveLogo writes to Worker metafield (logged-in) or localStorage
      // (guest); cart-submit (follow-up) depends on the id resolving to
      // a real saved-logo entry.
      var savedLogo = {
        id: result.logoId,
        filename: result.filename,
        url: result.url,
        type: 'upload',
        uploaded_at: Date.now(),
        sha: result.sha || '' // Doc 10 W1 — content hash of the uploaded bytes
      };

      JRS.LogoService.saveLogo(savedLogo).then(function (persistResult) {
        if (!persistResult || !persistResult.ok) {
          _showError('Could not save your logo. Please try again.');
          JRS.CustomizerPopup.fireAnalytics('pdp_customizer_logo_upload_failed', {
            step_id: STEP_ID,
            position_id: pid,
            error_reason: 'persist_failed',
            error_message: 'saveLogo returned ok=false'
          });
          return;
        }

        var canonical = savedLogo;
        if (Array.isArray(persistResult.logos)) {
          for (var i = 0; i < persistResult.logos.length; i++) {
            if (persistResult.logos[i] && persistResult.logos[i].id === savedLogo.id) {
              canonical = persistResult.logos[i];
              break;
            }
          }
        }

        var assignment = {
          source: 'upload',
          logoId: canonical.id,
          url: canonical.url,
          filename: canonical.filename,
          sha: canonical.sha || result.sha || '' // Doc 10 W1 — content hash for provenance
        };
        // Read the draft as it is NOW, not the snapshot taken when the upload
        // started. Committing onto the snapshot wiped every assignment made
        // while the upload ran (e.g. Same logo's Continue copies), so those
        // positions reached Confirm with no logo (logo_setup_missing).
        var liveState = JRS.CustomizerPopupState.getState();
        var liveDraft = liveState.draft || {};
        // Upload landed after the shopper left this position (Continue, Back,
        // or the next position in a Different logos loop): keep it in their
        // saved logos only. Review already shows the logo they chose.
        var stillHere = !!_mountEl &&
          liveState.currentStep === STEP_ID &&
          _resolveCurrentPositionId(liveDraft) === pid;
        if (stillHere) {
          _commitAssignment(liveDraft, pid, assignment);
          _swapToUploadPreview(canonical.filename, canonical.url);
          _updateCtaState(true);
        }

        // Doc 3 §7.1 — no PII in telemetry. A raw filename can carry a
        // customer/company name, so send only the file EXTENSION (png/jpg/
        // svg/…), which is a safe format signal. The full filename still
        // lives on the cart line for fulfilment; it just never reaches GA4.
        var _fn = canonical.filename || '';
        var _dot = _fn.lastIndexOf('.');
        var _fileExt = (_dot > -1 && (_fn.length - _dot) <= 6)
          ? _fn.slice(_dot + 1).toLowerCase()
          : '';
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_logo_upload', {
          step_id: STEP_ID,
          position_id: pid,
          logo_id: canonical.id,
          file_ext: _fileExt,
          persist_source: persistResult.source
        });
        _clearError();
      }).catch(function (persistErr) {
        if (window.console && console.warn) {
          console.warn('logo_picker: saveLogo threw', persistErr);
        }
        _showError('Could not save your logo. Please try again.');
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_logo_upload_failed', {
          step_id: STEP_ID,
          position_id: pid,
          error_reason: 'persist_failed',
          error_message: (persistErr && persistErr.message) || 'saveLogo rejected'
        });
      });
    }).catch(function (err) {
      if (progressEl) progressEl.hidden = true;
      var msg = (err && err.message) || 'Upload failed. Please try again.';
      _showError(msg);
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_logo_upload_failed', {
        step_id: STEP_ID,
        position_id: pid,
        error_reason: 'upload_failed',
        error_message: msg
      });
    });
  }

  function _onRemoveUpload() {
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var pid = _resolveCurrentPositionId(draft);
    if (!pid) return;

    // Remove the assignment from state.draft.logo_assignments.
    var existing = draft.logo_assignments || {};
    var next = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k) && k !== pid) next[k] = existing[k];
    }
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'logo_assignments', value: next }
    });

    _swapToDropzone();
    _updateCtaState(false);
  }

  function _onToggleExisting(e) {
    _disclosureExpanded = !_disclosureExpanded;
    var btn = e.currentTarget;
    btn.setAttribute('aria-expanded', _disclosureExpanded ? 'true' : 'false');
    var grid = _mountEl.querySelector('#jrs-existing-grid');
    if (grid) grid.setAttribute('data-expanded', _disclosureExpanded ? '1' : '0');
  }

  function _onSelectExistingClick(e) {
    var logoId = e.currentTarget && e.currentTarget.getAttribute('data-jrs-logo-id');
    if (!logoId) return;
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var pid = _resolveCurrentPositionId(draft);
    if (!pid) return;

    // 2026-05-20 Bug 4 fix — enrich the assignment with url + filename
    // (or previewText for text logos) from the saved-logos cache so the
    // review step's logo-group thumb + filename label render real data
    // instead of a generic placeholder. Previously only {source, logoId}
    // were written, so review fell through to the "LOGO" SVG fallback
    // and "Logo N" filename fallback.
    var assignment = { source: 'existing', logoId: logoId };
    var savedLogosResult = (JRS.LogoService && JRS.LogoService.readSavedLogos)
      ? JRS.LogoService.readSavedLogos()
      : null;
    var savedLogos = (savedLogosResult && savedLogosResult.logos) || [];
    var matched = null;
    for (var li = 0; li < savedLogos.length; li++) {
      if (savedLogos[li] && String(savedLogos[li].id) === String(logoId)) {
        matched = savedLogos[li];
        break;
      }
    }
    if (matched) {
      if (matched.type === 'text') {
        assignment.previewText = (matched.text_lines && matched.text_lines[0]) || '';
      } else {
        assignment.url = matched.url || '';
        assignment.filename = matched.filename || '';
      }
    }

    _commitAssignment(draft, pid, assignment);

    // Touch the logo's last_used_at on the server (Doc 4 v0.3 §5.4).
    if (JRS.LogoService && typeof JRS.LogoService.touchLogo === 'function') {
      JRS.LogoService.touchLogo(logoId);
    }

    // If an upload is currently in the preview, clear it — selecting an
    // existing logo replaces any current upload for this position.
    _swapToDropzone();

    // Update tile selected styling.
    var allCards = _mountEl.querySelectorAll('[data-jrs-action="select-existing"]');
    for (var i = 0; i < allCards.length; i++) {
      allCards[i].classList.remove('jrs-existing-card--selected');
      allCards[i].setAttribute('aria-checked', 'false');
    }
    e.currentTarget.classList.add('jrs-existing-card--selected');
    e.currentTarget.setAttribute('aria-checked', 'true');

    _updateCtaState(true);
    _clearError();

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'logo_source',
      decision_value: 'existing',
      position_id: pid
    });
  }

  function _onGoToTextBuilderClick() {
    JRS.CustomizerPopup.goToStep('logo_text_builder', { trigger: 'primary_cta' });
  }

  // Edit the committed text logo — returns to the builder, which rehydrates from
  // draft.text_logo in its onEnter. The assignment is left intact until the user
  // re-commits in the builder.
  function _onEditTextClick() {
    JRS.CustomizerPopup.goToStep('logo_text_builder', { trigger: 'edit_text_logo' });
  }

  // Remove the committed text logo for this position (source-agnostic assignment
  // removal, mirroring _onRemoveUpload), drop the card, and restore the dropzone.
  function _onRemoveText() {
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var pid = _resolveCurrentPositionId(draft);
    if (!pid) return;
    var existing = draft.logo_assignments || {};
    var next = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k) && k !== pid) next[k] = existing[k];
    }
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'logo_assignments', value: next }
    });
    var card = _mountEl && _mountEl.querySelector('[data-jrs-text-added]');
    if (card && card.parentNode) card.parentNode.removeChild(card);
    _swapToDropzone();      // un-hide the dropzone (also resets the upload preview — harmless)
    _updateCtaState(false); // disable Continue until a new logo is chosen
  }

  function _onContinueClick() {
    _syncNoteToAssignment(); // §3 — flush un-blurred note text into state first
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;

    var draft = state.draft || {};

    // If strategy='same_logo', copy assignment to every other position
    // (so review renders consistently).
    if (draft.logo_strategy === 'same_logo') {
      var pid = _resolveCurrentPositionId(draft);
      var sourceAssignment = (draft.logo_assignments || {})[pid];
      if (sourceAssignment) {
        var allAssignments = {};
        var existing = draft.logo_assignments || {};
        for (var ek in existing) {
          if (Object.prototype.hasOwnProperty.call(existing, ek)) allAssignments[ek] = existing[ek];
        }
        var positions = draft.positions || [];
        for (var i = 0; i < positions.length; i++) {
          allAssignments[positions[i]] = sourceAssignment;
        }
        JRS.CustomizerPopupState.dispatch({
          type: 'SET_DRAFT_FIELD',
          payload: { field: 'logo_assignments', value: allAssignments }
        });
      }
    }

    // Loop logo_picker per-position when strategy='different_logos'.
    var nextStep = 'review';
    var loopingSameStep = false;
    if (draft.logo_strategy === 'different_logos') {
      var nextCursor = (typeof draft._position_cursor === 'number' ? draft._position_cursor : 0) + 1;
      var positions2 = draft.positions || [];
      if (nextCursor < positions2.length) {
        JRS.CustomizerPopupState.dispatch({
          type: 'SET_DRAFT',
          payload: {
            draft: _merge(draft, {
              _position_cursor: nextCursor,
              _current_position_for_logo: positions2[nextCursor]
            })
          }
        });
        nextStep = 'logo_picker';
        loopingSameStep = true;
      }
    }

    // Per-product bundle (Phase per_product S1): this category's per-position
    // flow is done (nextStep is still 'review'). Delegate the category-complete
    // decision to the flow orchestrator — it snapshots this category's config,
    // advances the cursor, and returns 'position_picker' (next category, flat
    // scratch reset) or 'review' (all categories done). bundle_wide / multistep
    // are unaffected (branding_strategy !== 'per_product' returns 'review').
    if (nextStep === 'review' &&
        draft.branding_strategy === 'per_product' &&
        window.JRS && JRS.FlowBundle &&
        typeof JRS.FlowBundle.completeCategoryOrAdvance === 'function') {
      nextStep = JRS.FlowBundle.completeCategoryOrAdvance(state);
    }

    // 2026-05-20 Bug 3 fix — when the next step is logo_picker itself,
    // the shell's same-step guard would no-op the navigation. Pass
    // forceReentry:true so the reducer bumps _reentryEpoch and the shell
    // re-fires onExit/onEnter for the next position. Without this, the
    // cursor advances in state.draft but the UI freezes on position 0
    // and validation fails because logo_picker is reading position 1's
    // assignment which doesn't exist yet.
    JRS.CustomizerPopup.goToStep(nextStep, {
      trigger: 'primary_cta',
      forceReentry: loopingSameStep
    });
  }

  // §3 — push the notes textarea's current value onto the current position's
  // committed assignment (no-op when nothing is committed yet; _commitAssignment
  // captures the note in that ordering instead).
  function _syncNoteToAssignment() {
    var input = _mountEl && _mountEl.querySelector('[data-jrs-action="notes-input"]');
    if (!input) return;
    var state = JRS.CustomizerPopupState.getState();
    var draft = (state && state.draft) || {};
    var pid = _resolveCurrentPositionId(draft);
    var existing = draft.logo_assignments || {};
    var current = existing[pid];
    if (!current || (current.note || '') === input.value) return;
    var next = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) next[k] = existing[k];
    }
    next[pid] = _merge(current, { note: input.value });
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'logo_assignments', value: next }
    });
  }

  function _commitAssignment(draft, positionId, assignment) {
    // §3 — carry any note already typed for this position: keep the live
    // textarea value, else the note from the assignment being replaced.
    if (assignment && assignment.note == null) {
      var noteInput = _mountEl && _mountEl.querySelector('[data-jrs-action="notes-input"]');
      var prior = (draft.logo_assignments || {})[positionId];
      assignment.note = (noteInput && noteInput.value) || (prior && prior.note) || '';
    }
    var existing = draft.logo_assignments || {};
    var next = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) next[k] = existing[k];
    }
    next[positionId] = assignment;
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'logo_assignments', value: next }
    });
  }

  // ---------------------------------------------------------------------
  // Login nudge (Doc 4 §7 — guest-only, dismissible, three placements
  // across the popup; two of them live in this controller)
  // ---------------------------------------------------------------------

  function _renderLoginNudge(placement, message, opts) {
    // Guest login nudges disabled — merchant decision 2026-07-08.
    return '';
  }

  function _bindNudgeInteractions() {
    if (!_mountEl) return;
    var ctas = _mountEl.querySelectorAll('[data-jrs-action="login-nudge-cta"]');
    for (var i = 0; i < ctas.length; i++) _bind(ctas[i], 'click', _onNudgeCtaClick);
    var dismissBtns = _mountEl.querySelectorAll('[data-jrs-action="login-nudge-dismiss"]');
    for (var j = 0; j < dismissBtns.length; j++) _bind(dismissBtns[j], 'click', _onNudgeDismissClick);
  }

  function _onNudgeCtaClick(e) {
    var placement = e.currentTarget && e.currentTarget.getAttribute('data-placement');
    if (placement && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_clicked', {
        step_id: STEP_ID,
        placement: placement
      });
    }
    // Allow the anchor's default navigation to proceed (full page load to
    // /account/login?return_url=...). We do not preventDefault.
  }

  function _onNudgeDismissClick(e) {
    var btn = e.currentTarget;
    var placement = btn && btn.getAttribute('data-placement');
    var nudgeEl = btn && (btn.closest ? btn.closest('[data-jrs-login-nudge]') : null);
    if (nudgeEl && nudgeEl.parentNode) nudgeEl.parentNode.removeChild(nudgeEl);
    if (placement) _markNudgeDismissed(placement);
    if (placement && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_dismissed', {
        step_id: STEP_ID,
        placement: placement
      });
    }
  }

  function _firePostRenderShownPass() {
    if (!_mountEl) return;
    var nudges = _mountEl.querySelectorAll('[data-jrs-login-nudge]');
    for (var i = 0; i < nudges.length; i++) {
      if (nudges[i].classList.contains('jrs-login-nudge--hidden')) continue;
      var placement = nudges[i].getAttribute('data-placement');
      if (placement) _maybeFireNudgeShown(placement);
    }
  }

  function _maybeFireNudgeShown(placement) {
    if (!placement || _shownNudgesThisMount[placement]) return;
    _shownNudgesThisMount[placement] = true;
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_shown', {
        step_id: STEP_ID,
        placement: placement
      });
    }
  }

  function _isNudgeDismissed(placement) {
    return !!(window.JRS && window.JRS._dismissedNudges && window.JRS._dismissedNudges[placement]);
  }

  function _markNudgeDismissed(placement) {
    window.JRS = window.JRS || {};
    window.JRS._dismissedNudges = window.JRS._dismissedNudges || {};
    window.JRS._dismissedNudges[placement] = true;
  }

  function _currentReturnUrl() {
    try {
      var loc = window.location || {};
      return (loc.pathname || '/') + (loc.search || '') + (loc.hash || '');
    } catch (_e) {
      return '/';
    }
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _formatRelativeDate(ts) {
    if (!ts) return '';
    var now = Date.now();
    var diff = now - ts;
    var day = 86400000;
    if (diff < day) return 'Used today';
    if (diff < 2 * day) return 'Used yesterday';
    var days = Math.floor(diff / day);
    if (days < 14) return 'Used ' + days + ' days ago';
    if (days < 60) return 'Used ' + Math.floor(days / 7) + ' weeks ago';
    if (days < 365) return 'Used ' + Math.floor(days / 30) + ' months ago';
    return 'Used over a year ago';
  }

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Preview src for a logo URL: PDFs (and any non-image type) can't render in
   * an <img>, so substitute the shared document placeholder
   * (JRS.LogoService.previewSrcFor — control-theme parity, 2026-08-01).
   * Falls back to the raw URL if the service isn't loaded.
   */
  function _previewSrc(url, nameHint) {
    if (JRS.LogoService && typeof JRS.LogoService.previewSrcFor === 'function') {
      return JRS.LogoService.previewSrcFor(url, nameHint);
    }
    return url;
  }

  function _merge(a, b) {
    var out = {};
    for (var k1 in a) {
      if (Object.prototype.hasOwnProperty.call(a, k1)) out[k1] = a[k1];
    }
    for (var k2 in b) {
      if (Object.prototype.hasOwnProperty.call(b, k2)) out[k2] = b[k2];
    }
    return out;
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.LogoPicker = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    _categoryProgress: _categoryProgress
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
